import { Loader as Loader2, Target, ExternalLink, TriangleAlert as AlertTriangle } from 'lucide-react';
import type { StrategicGoal } from '../../types';

interface Props {
  goals: StrategicGoal[] | null;
  status: 'pending' | 'processing' | 'completed' | 'error' | null;
  companyName: string;
}

const CATEGORY_STYLE: Record<StrategicGoal['category'], string> = {
  Growth: 'bg-forest-50 text-forest-700 border-forest-200',
  'M&A': 'bg-app-warning/10 text-app-warning border-app-warning',
  Geographic: 'bg-app-focus/10 text-app-focus border-app-focus',
  Talent: 'bg-app-focus/10 text-app-focus border-app-focus/30',
  Product: 'bg-app-focus/10 text-app-focus border-app-focus/30',
  Financial: 'bg-app-positive/10 text-app-positive border-app-positive',
  ESG: 'bg-lime-50 text-lime-700 border-lime-200',
  Other: 'bg-app-canvas text-app-ink border-app-line',
};

export default function StrategicGoalsSection({ goals, status, companyName }: Props) {
  const isProcessing = status === 'processing';
  const isError = status === 'error';
  const hasGoals = Array.isArray(goals) && goals.length > 0;

  if (!isProcessing && !hasGoals && status !== 'completed' && !isError) {
    return null;
  }

  return (
    <div className="bg-app-surface rounded-lg border border-forest-100 shadow-none p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Target className="w-5 h-5 text-forest-600" />
          <h3 className="text-sm font-semibold text-app-ink">Strategic Goals</h3>
          {hasGoals && (
            <span className="text-xs text-app-muted">
              Publicly stated goals for {companyName}
            </span>
          )}
        </div>
        {isProcessing && (
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-app-warning">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Researching
          </span>
        )}
      </div>

      {isProcessing && !hasGoals && (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2].map(i => (
            <div key={i} className="rounded-lg border border-app-line bg-app-canvas/50 p-4 animate-pulse">
              <div className="h-3 w-24 bg-app-line rounded mb-3" />
              <div className="h-2 w-full bg-app-line rounded mb-2" />
              <div className="h-2 w-3/4 bg-app-line rounded" />
            </div>
          ))}
        </div>
      )}

      {!isProcessing && isError && !hasGoals && (
        <div className="flex items-start gap-2 p-3 bg-app-critical/10 border border-app-critical rounded-lg">
          <AlertTriangle className="w-4 h-4 text-app-critical mt-0.5 flex-shrink-0" />
          <p className="text-xs text-app-critical">
            Could not extract strategic goals. Re-run deep research to try again.
          </p>
        </div>
      )}

      {!isProcessing && status === 'completed' && !hasGoals && (
        <p className="text-xs text-app-muted italic">
          No publicly cited strategic goals found for this company.
        </p>
      )}

      {hasGoals && (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {goals!.map((g, i) => (
            <a
              key={`${g.title}-${i}`}
              href={g.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="group rounded-lg border border-app-line bg-app-surface p-4 transition hover:border-forest-300 hover:shadow-none"
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <p className="text-sm font-semibold text-forest-700 group-hover:text-forest-800 leading-tight">
                  {g.title}
                </p>
                <ExternalLink className="w-3.5 h-3.5 text-app-line-strong group-hover:text-forest-500 flex-shrink-0 mt-0.5" />
              </div>
              <p className="text-xs text-app-ink leading-relaxed mb-3">{g.statement}</p>
              <div className="flex flex-wrap items-center gap-1.5">
                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${CATEGORY_STYLE[g.category] || CATEGORY_STYLE.Other}`}>
                  {g.category}
                </span>
                {g.target_metric && (
                  <span className="text-[10px] text-app-muted font-medium">{g.target_metric}</span>
                )}
                {g.target_date && (
                  <span className="text-[10px] text-app-muted">by {g.target_date}</span>
                )}
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
